import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  Presentation, 
  HelpCircle, 
  FileCheck, 
  Monitor, 
  CheckCircle, 
  ArrowRight,
  Building2,
  Building,
  GraduationCap,
  Users,
  Award,
  Linkedin,
  Mail
} from 'lucide-react';
import SEO from '../components/SEO';

const Institutional = () => {
  return (
    <div style={{ paddingTop: '80px' }}>
      <SEO 
        title="Institutional CIRO Exam Training - Bulk Packages for Universities & Firms | Canada"
        description="Corporate and institutional CIRO exam prep packages. Bulk discounts for 5+ seats. LMS-compatible materials for educational institutes & financial firms. CIRE, RSE, ISE, Derivatives training. 800+ question banks, mock exams. D2L uploadable. Serving Canadian universities and training programs."
        keywords="CIRO institutional training, CIRO corporate training, CIRO bulk orders, CIRO university program, CIRO LMS materials, D2L CIRO course, CIRO question bank, CIRO exam training program, institutional securities training Canada, corporate exam prep Canada, bulk CIRO exam prep, CIRO training for firms, CIRO approved person conduct training, CIRO continuing education requirements"
        ogTitle="Institutional CIRO Exam Training - Bulk Packages for Organizations"
        ogDescription="Equip your team with comprehensive CIRO exam prep. Bulk discounts, LMS-compatible materials, question banks. Perfect for universities & financial firms."
      />

      {/* Hero Section */}
      <section style={{
        padding: '100px 0 80px',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.5
        }}></div>
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: 'rgba(212, 175, 55, 0.2)',
              border: '1px solid var(--gold-primary)',
              padding: '8px 20px',
              borderRadius: '50px',
              marginBottom: '24px'
            }}>
              <Building2 size={18} color="var(--gold-primary)" />
              <span style={{ color: 'var(--gold-primary)', fontWeight: 600, fontSize: '0.9rem' }}>FOR ORGANIZATIONS</span>
            </div>
            <h1 style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
              fontWeight: 800,
              marginBottom: '24px',
              lineHeight: 1.2
            }}>
              Equip Your Team for the <span style={{ color: 'var(--gold-primary)' }}>Future of Financial Licensing</span>
            </h1>
            <p style={{
              fontSize: '1.25rem',
              lineHeight: 1.7,
              marginBottom: '40px',
              color: 'rgba(255,255,255,0.9)'
            }}>
              As the Canadian financial landscape evolves with the new CIRO proficiency model, ensure your students and associates are prepared to succeed on day one.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="mailto:info@vittafinance.ca" className="btn-gold" style={{ padding: '16px 32px', fontSize: '1rem' }}>
                <Mail size={20} /> Request a Quote
              </a>
              <a href="#packages" className="btn-secondary" style={{ 
                backgroundColor: 'white', 
                color: 'var(--brand-primary)',
                padding: '16px 32px',
                fontSize: '1rem'
              }}>
                View Packages <ArrowRight size={20} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section style={{
        padding: '80px 0',
        backgroundColor: 'var(--bg-page)'
      }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '40px',
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            <div style={{
              padding: '40px',
              backgroundColor: 'var(--bg-card)',
              borderRadius: '16px',
              border: '2px solid var(--brand-primary)',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                backgroundColor: 'rgba(30, 58, 138, 0.1)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}>
                <GraduationCap size={40} color="var(--brand-primary)" />
              </div>
              <h3 className="heading-4" style={{ marginBottom: '16px' }}>Educational Institutes</h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '20px' }}>
                Integrate industry-standard CIRO exam prep into your finance curriculum with LMS-compatible materials.
              </p>
              <Link 
                to="/educational-institutes" 
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: 'var(--brand-primary)',
                  fontWeight: 600,
                  textDecoration: 'none',
                  fontSize: '0.95rem'
                }}
              >
                Order Study Guides <ArrowRight size={18} />
              </Link>
            </div>
            <div style={{
              padding: '40px',
              backgroundColor: 'var(--bg-card)',
              borderRadius: '16px',
              border: '2px solid var(--gold-primary)',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                backgroundColor: 'rgba(212, 175, 55, 0.1)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}>
                <Building2 size={40} color="var(--gold-primary)" />
              </div>
              <h3 className="heading-4" style={{ marginBottom: '16px' }}>Financial Services Firms</h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                Accelerate your team's certification journey with comprehensive training programs designed for professionals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section style={{
        padding: '96px 0',
        backgroundColor: 'var(--bg-card)'
      }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="heading-2" style={{ marginBottom: '16px' }}>Comprehensive Institutional Package</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              Everything your organization needs for successful CIRO exam preparation
            </p>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            maxWidth: '1100px',
            margin: '0 auto'
          }}>
            {[
              {
                icon: <BookOpen size={36} color="var(--brand-primary)" />,
                title: 'Printed Textbooks',
                desc: 'Detailed, high-quality study guides covering every competency required for certification success.'
              },
              {
                icon: <HelpCircle size={36} color="var(--brand-primary)" />,
                title: 'Extensive Question Bank',
                desc: 'Over 800+ practice questions with detailed explanations to reinforce learning and build confidence.'
              },
              {
                icon: <FileCheck size={36} color="var(--brand-primary)" />,
                title: 'Full Mock Exams',
                desc: '5 comprehensive mock exams with 550+ questions simulating the real testing environment.'
              },
              {
                icon: <Monitor size={36} color="var(--brand-primary)" />,
                title: 'LMS Compatible',
                desc: 'All digital assets are D2L uploadable for seamless integration into your Learning Management System.'
              },
              {
                icon: <Users size={36} color="var(--brand-primary)" />,
                title: 'Video Package Add-on',
                desc: 'Boost engagement with instructor-led video lessons available as an optional add-on package.'
              }
            ].map((item, idx) => (
              <div key={idx} style={{
                padding: '32px',
                backgroundColor: 'var(--bg-page)',
                borderRadius: '12px',
                border: '1px solid var(--border-light)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }} className="card-hover-effect">
                <div style={{ marginBottom: '20px' }}>{item.icon}</div>
                <h3 className="heading-5" style={{ marginBottom: '12px' }}>{item.title}</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="packages" style={{
        padding: '96px 0',
        backgroundColor: 'var(--bg-page)'
      }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="heading-2" style={{ marginBottom: '16px' }}>Flexible Pricing Options</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              All prices in CAD • Volume discounts available
            </p>
          </div>

          {/* Individual Packages */}
          <div style={{ marginBottom: '60px' }}>
            <h3 className="heading-4" style={{ textAlign: 'center', marginBottom: '32px', color: 'var(--text-secondary)' }}>
              Individual Exam Packages
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '24px',
              maxWidth: '1100px',
              margin: '0 auto'
            }}>
              {[
                { name: 'CIRE Package', price: '$269.99', link: '/cire-exam' },
                { name: 'RSE Package', price: '$269.99', link: '/rse-exam' },
                { name: 'ISE Package', price: '$269.99', link: '/ise-exam' },
                { name: 'Derivatives Package', price: '$269.99', link: '/derivatives-exam' }
              ].map((pkg, idx) => (
                <div key={idx} style={{
                  padding: '32px',
                  backgroundColor: 'var(--bg-card)',
                  borderRadius: '12px',
                  border: '1px solid var(--border-light)',
                  textAlign: 'center'
                }}>
                  <h4 className="heading-5" style={{ marginBottom: '16px' }}>{pkg.name}</h4>
                  <div style={{ 
                    fontSize: '2.5rem', 
                    fontWeight: 800, 
                    color: 'var(--brand-primary)',
                    marginBottom: '8px'
                  }}>{pkg.price}</div>
                  <p className="body-small" style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>per seat</p>
                  <Link to={pkg.link} style={{
                    color: 'var(--brand-primary)',
                    fontWeight: 600,
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '0.9rem'
                  }}>
                    Learn More <ArrowRight size={16} />
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* Bundle Deals */}
          <div>
            <h3 className="heading-4" style={{ textAlign: 'center', marginBottom: '32px', color: 'var(--text-secondary)' }}>
              Bundle Deals — Save More
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '32px',
              maxWidth: '900px',
              margin: '0 auto'
            }}>
              {/* Bundle 1 */}
              <div style={{
                padding: '40px',
                backgroundColor: 'var(--brand-primary)',
                borderRadius: '16px',
                textAlign: 'center',
                color: 'white',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-14px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'var(--gold-primary)',
                  color: 'white',
                  padding: '8px 24px',
                  borderRadius: '20px',
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(212, 175, 55, 0.4)'
                }}>SAVE ~20%</div>
                <h4 className="heading-4" style={{ marginBottom: '8px', color: 'white' }}>CIRE + RSE Bundle</h4>
                <p style={{ fontSize: '0.9rem', marginBottom: '20px', opacity: 0.8 }}>Two essential exams, one great price</p>
                <div style={{ 
                  fontSize: '3rem', 
                  fontWeight: 900, 
                  color: 'var(--gold-primary)',
                  marginBottom: '8px'
                }}>$499.00</div>
                <p style={{ fontSize: '0.9rem', marginBottom: '24px', opacity: 0.8 }}>per seat</p>
                <ul style={{ listStyle: 'none', padding: 0, marginBottom: '24px', textAlign: 'left' }}>
                  {['CIRE Complete Package', 'RSE Complete Package', 'All Textbooks & Materials', 'Full Question Banks', 'Mock Exams Included'].map((item, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <CheckCircle size={18} color="var(--gold-primary)" />
                      <span style={{ fontSize: '0.95rem' }}>{item}</span>
                    </li>
                  ))}
                </ul>
                <a href="mailto:info@vittafinance.ca" className="btn-gold" style={{ width: '100%' }}>
                  Get This Bundle
                </a>
              </div>

              {/* Bundle 2 */}
              <div style={{
                padding: '40px',
                background: 'linear-gradient(135deg, var(--brand-primary) 0%, #1e40af 100%)',
                borderRadius: '16px',
                textAlign: 'center',
                color: 'white',
                position: 'relative',
                boxShadow: '0 12px 40px rgba(30, 58, 138, 0.3)'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-14px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'linear-gradient(135deg, var(--gold-primary) 0%, #b8860b 100%)',
                  color: 'white',
                  padding: '8px 24px',
                  borderRadius: '20px',
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(212, 175, 55, 0.4)'
                }}>BEST VALUE</div>
                <h4 className="heading-4" style={{ marginBottom: '8px', color: 'white' }}>Complete Bundle</h4>
                <p style={{ fontSize: '0.9rem', marginBottom: '20px', opacity: 0.8 }}>CIRE + RSE + ISE + Derivatives</p>
                <div style={{ 
                  fontSize: '3rem', 
                  fontWeight: 900, 
                  color: 'var(--gold-primary)',
                  marginBottom: '8px'
                }}>$863.99</div>
                <p style={{ fontSize: '0.9rem', marginBottom: '24px', opacity: 0.8 }}>per seat • Save ~20%</p>
                <ul style={{ listStyle: 'none', padding: 0, marginBottom: '24px', textAlign: 'left' }}>
                  {['All 4 Exam Packages', 'Complete Textbook Library', 'Full Question Banks (800+)', '20 Mock Exams Total'].map((item, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <CheckCircle size={18} color="var(--gold-primary)" />
                      <span style={{ fontSize: '0.95rem' }}>{item}</span>
                    </li>
                  ))}
                </ul>
                <a href="mailto:info@vittafinance.ca" className="btn-gold" style={{ width: '100%' }}>
                  Get Complete Bundle
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Partner Section */}
      <section style={{
        padding: '96px 0',
        backgroundColor: 'var(--bg-card)'
      }}>
        <div className="container">
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '60px',
            alignItems: 'center'
          }}>
            <div>
              <h2 className="heading-2" style={{ marginBottom: '24px' }}>
                Why Partner with <span style={{ color: 'var(--brand-primary)' }}>Vitta Finance?</span>
              </h2>
              <p className="body-medium" style={{ lineHeight: 1.8, marginBottom: '24px' }}>
                We move beyond rote memorization. Our content uses the <strong>"Financial Doctor" methodology</strong> to teach learners how to apply regulations in real-world scenarios—exactly what the new competency-based exams demand.
              </p>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {[
                  'Scenario-based learning approach',
                  'Real-world application focus',
                  'Competency-aligned curriculum',
                  'Proven industry leading pass rate track record'
                ].map((item, idx) => (
                  <li key={idx} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px', 
                    marginBottom: '16px',
                    fontSize: '1rem'
                  }}>
                    <Award size={20} color="var(--gold-primary)" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '40px',
              borderRadius: '16px',
              border: '1px solid var(--border-light)'
            }}>
              <h3 className="heading-4" style={{ marginBottom: '24px' }}>Get Started Today</h3>
              <p className="body-medium" style={{ marginBottom: '32px', color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                Ready to equip your team or students with industry-leading CIRO exam prep? Let's discuss your organization's needs.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <a 
                  href="mailto:info@vittafinance.ca" 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '16px 24px',
                    backgroundColor: 'var(--brand-primary)',
                    color: 'white',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    transition: 'transform 0.2s ease'
                  }}
                >
                  <Mail size={20} />
                  info@vittafinance.ca
                </a>
                <a 
                  href="https://www.linkedin.com/in/amina-beecroft/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '16px 24px',
                    backgroundColor: '#0077b5',
                    color: 'white',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    transition: 'transform 0.2s ease'
                  }}
                >
                  <Linkedin size={20} />
                  Connect with Amina on LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Discounts Section */}
      <section style={{
        padding: '96px 0',
        backgroundColor: 'var(--bg-page)'
      }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(212, 175, 55, 0.15)',
              border: '1px solid rgba(212, 175, 55, 0.3)',
              borderRadius: '20px',
              padding: '6px 16px',
              marginBottom: '16px'
            }}>
              <Building size={16} color="var(--gold-primary)" />
              <span style={{ color: 'var(--gold-primary)', fontSize: '12px', fontWeight: '600', letterSpacing: '0.5px' }}>SPECIAL OFFER</span>
            </div>
            <h2 className="heading-2" style={{ marginBottom: '16px' }}>
              Corporate <span style={{ color: 'var(--brand-primary)' }}>Discounts</span>
            </h2>
            <p className="body-medium" style={{ maxWidth: '600px', margin: '0 auto', color: 'var(--text-secondary)' }}>
              Special pricing for firms and institutions. One-time payment per candidate with lifetime access to all study materials. Does not include printed book.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            {[
              { title: 'CIRE', subtitle: 'Canadian Investment Regulatory Exam', price: '$110.00', color: '#1e40af' },
              { title: 'RSE', subtitle: 'Retail Securities Exam', price: '$125.00', color: '#166534' },
              { title: 'ISE', subtitle: 'Institutional Securities Exam', price: '$125.00', color: '#7c3aed' },
              { title: 'Derivatives', subtitle: 'CIRO Derivative Exam', price: '$125.00', color: '#92400e' },
              { title: 'Traders', subtitle: 'CIRO Traders Licensing Exam', price: '$175.00', color: '#0d9488', comingSoon: true }
            ].map((pkg, idx) => (
              <div key={idx} style={{
                background: 'white',
                borderRadius: '16px',
                padding: '32px',
                border: '1px solid var(--border-light)',
                textAlign: 'center',
                position: 'relative'
              }}>
                {pkg.comingSoon && (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
                    color: 'white',
                    padding: '4px 10px',
                    borderRadius: '10px',
                    fontSize: '0.65rem',
                    fontWeight: '700'
                  }}>COMING SOON</div>
                )}
                <h3 style={{
                  fontFamily: 'Georgia, serif',
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: pkg.color,
                  marginBottom: '8px'
                }}>{pkg.title}</h3>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '20px' }}>{pkg.subtitle}</p>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: '800',
                  color: 'var(--text-primary)',
                  marginBottom: '8px'
                }}>{pkg.price} <span style={{ fontSize: '0.9rem', fontWeight: '500', color: 'var(--text-secondary)' }}>CAD</span></div>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)', marginBottom: '20px' }}>per candidate</p>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  fontSize: '0.85rem',
                  color: 'var(--text-secondary)',
                  marginBottom: '24px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <CheckCircle size={16} color="#22c55e" />
                    <span>Video Course + Question Bank</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <CheckCircle size={16} color="#22c55e" />
                    <span>Interactive Study Guide</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <CheckCircle size={16} color="#22c55e" />
                    <span>Lifetime Access</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <Link
              to="/corporate-discounts"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '16px 32px',
                background: 'var(--brand-primary)',
                color: 'white',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                textDecoration: 'none',
                transition: 'transform 0.2s'
              }}
            >
              View Corporate Pricing
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Institutional;
